import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Flex, Text, TextField, Select, Callout } from '@radix-ui/themes'
import { ScanDataPoint, ScanDataPointSchema } from '@willie/schemas'
import { WaterStation } from '~/features/stations/schemas'
import { z } from 'zod'
import { LuBadgeInfo } from 'react-icons/lu'
import DatePickerField from '~/components/DatePickerField'
import { DateTime } from 'luxon'
import { FieldWrapper } from '~/components/FormFieldWrapper'

type DatapointFormProps = {
  isPending: boolean
  datapoint: ScanDataPoint
  stations: WaterStation[]
  onSubmit: (data: ScanDataPoint) => void
}

const datapointFormSchema = ScanDataPointSchema.pick({
  volume: true,
  pumpStationID: true,
  date: true
})

type DatapointFormSchema = z.infer<typeof datapointFormSchema>

const DatapointForm = ({ isPending, datapoint, stations, onSubmit }: DatapointFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, isDirty }
  } = useForm<DatapointFormSchema>({
    resolver: zodResolver(datapointFormSchema),
    defaultValues: {
      volume: datapoint.volume,
      pumpStationID: datapoint.pumpStationID,
      date: datapoint.date
    },
    mode: 'onChange'
  })

  const handleFormSubmit = (data: DatapointFormSchema) => {
    // Combine the form data with the existing datapoint
    onSubmit({
      ...datapoint,
      volume: data.volume,
      pumpStationID: data.pumpStationID,
      date: data.date
    })
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Flex gap="6" direction="column" mt={'4'} py={'6'} className="h-[80svh] sm:h-full">
        <Callout.Root variant="soft" color="gray">
          <Callout.Icon>
            <LuBadgeInfo size={20} />
          </Callout.Icon>
          <Callout.Text>
            <Text as="p" weight={'medium'} mb={'1'} size={'2'}>
              Raisonnement
            </Text>
            {datapoint.reasoning}
          </Callout.Text>
        </Callout.Root>

        <Flex direction={'column'} gap="4">
          <FieldWrapper label="Date" htmlFor="date">
            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <DatePickerField
                  size={'3'}
                  id="date"
                  value={DateTime.fromISO(field.value).toJSDate()}
                  onChange={(date) => {
                    field.onChange(date.toISOString())
                  }}
                />
              )}
            />
          </FieldWrapper>
          <FieldWrapper label="Station de pompage" htmlFor="pumpStation">
            <Controller
              control={control}
              name="pumpStationID"
              render={({ field }) => (
                <Select.Root size={'3'} value={field.value} onValueChange={(value) => field.onChange(value)}>
                  <Select.Trigger id="pumpStation" />
                  <Select.Content variant="soft" position="item-aligned" color="gray">
                    {stations.map((station) => (
                      <Select.Item value={station.id} key={station.id}>
                        <Flex gap="2" align={'center'}>
                          {station.name}
                        </Flex>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              )}
            />
          </FieldWrapper>
          <FieldWrapper label="Volume" htmlFor="volume">
            <TextField.Root
              color="gray"
              size={'3'}
              id="volume"
              variant="surface"
              placeholder="Entrez le volume"
              type="number"
              {...register('volume', { valueAsNumber: true })}
            />
          </FieldWrapper>
        </Flex>

        <Button type="submit" disabled={!isValid || !isDirty} size={'4'} loading={isPending} className="ml-auto">
          Enregistrer
        </Button>
      </Flex>
    </form>
  )
}

// Helper component for form fields to maintain consistent spacing and structure

export default DatapointForm
